import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Search, Trash2, Loader2, Inbox, PhoneCall, GraduationCap, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS = ["new", "contacted", "admitted", "closed"];

const statusStyle = {
  new: "bg-blue-50 text-blue-800 border-blue-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  admitted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminDashboard() {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, admitted: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/stats"),
      ]);
      setInquiries(list);
      setStats(s);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Inquiry deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return inquiries.filter((i) => {
      const matchFilter = filter === "all" ? true : i.status === filter;
      const matchQ = !term
        ? true
        : [i.name, i.email, i.phone, i.course].some((v) => (v || "").toLowerCase().includes(term));
      return matchFilter && matchQ;
    });
  }, [inquiries, q, filter]);

  const statCards = [
    { label: "Total Inquiries", value: stats.total, icon: Inbox, tint: "bg-blue-50 text-blue-800" },
    { label: "New", value: stats.new, icon: Inbox, tint: "bg-rose-50 text-rose-600" },
    { label: "Contacted", value: stats.contacted, icon: PhoneCall, tint: "bg-amber-50 text-amber-700" },
    { label: "Admitted", value: stats.admitted, icon: CheckCircle2, tint: "bg-emerald-50 text-emerald-700" },
    { label: "Closed", value: stats.closed, icon: CheckCircle2, tint: "bg-slate-100 text-slate-600" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Overview</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Admissions Inquiries</h1>
        <p className="text-slate-500 mt-2">Track and manage prospective student submissions.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{s.label}</p>
                <p className="font-heading text-3xl text-slate-900 mt-2 number-block">{s.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl grid place-items-center ${s.tint}`}>
                <s.icon className="w-4 h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="admin-search" placeholder="Search by name, email, phone or course..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11 rounded-xl border-slate-200" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-filter-status" className="w-full sm:w-48 h-11 rounded-xl border-slate-200">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-16 grid place-items-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <GraduationCap className="w-10 h-10 mx-auto text-slate-300" />
              <p className="mt-3 font-heading text-lg text-slate-700">No inquiries yet</p>
              <p className="text-sm text-slate-500">Submissions from the website will appear here.</p>
            </div>
          ) : (
            <Table data-testid="inquiries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((i) => (
                  <TableRow key={i.id} data-testid={`inquiry-row-${i.id}`} className="hover:bg-slate-50/50">
                    <TableCell>
                      <div className="font-medium text-slate-900">{i.name}</div>
                      {i.message && (<div className="text-xs text-slate-500 mt-0.5 line-clamp-2 max-w-xs">{i.message}</div>)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-800">{i.email}</div>
                      <div className="text-xs text-slate-500">{i.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-800">{i.course}</TableCell>
                    <TableCell>
                      <Select value={i.status} onValueChange={(v) => handleStatus(i.id, v)}>
                        <SelectTrigger data-testid={`status-select-${i.id}`} className={`h-9 w-36 rounded-full border capitalize text-xs font-semibold ${statusStyle[i.status] || ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(i.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`delete-btn-${i.id}`} variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete inquiry?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove <strong>{i.name}</strong>&rsquo;s inquiry. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`delete-cancel-${i.id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction data-testid={`delete-confirm-${i.id}`} onClick={() => handleDelete(i.id)} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
